/**
 * The pipeline run: what one call to `inboxService.process` does.
 *
 *   1. reconcile — everything the stored facts settle, without AI
 *      (`reconcileCompany`), with the bank rows that need a classification
 *      held back when AI is about to provide one;
 *   2. AI — read every new receipt that has no stored extraction yet, and
 *      classify the held bank rows, in batches, with 429 backoff;
 *   3. reconcile again — the new readings and classifications pair with
 *      everything else in the company.
 *
 * Reading is the expensive step and happens once per document: a receipt
 * that already carries an `extraction` skips step 2 and only takes part in
 * the reconciliation, so a bank import never sends the pile to the AI again.
 *
 * Concurrency 1–3 workers for the reads, an AbortSignal that stops between
 * items, and per-item status transitions:
 *   new → processing → ready | check | needs_you | booked (auto-reconciled)
 *   new → processing → new     (rate limit / abort: waits, with a reason)
 *   new → processing → error   (anything else, Swedish message)
 * Items a crashed run left in `processing` are reclaimed after
 * `STALE_PROCESSING_MS`; the service serialises runs per company so two runs
 * in one tab never read the same receipt twice.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { getSettings } from '../settings/service';
import type { AiSettings, BankRow, BankRowClassification, InboxItem, IsoDate, ReceiptExtraction, Uuid } from '../types';
import type { BankRowClassificationResult, InboxDeps, ProcessSummary } from './internal-types';
import {
  BANK_CLASSIFY_BATCH,
  BANK_HISTORY_LIMIT,
  FILE_MISSING,
  GENERIC_AI_ERROR,
  PipelineAbort,
  PipelineWait,
  WAITING_FOR_AI,
  aiConfigured,
  fileLateDocument,
  loadCompanyContext,
  patchItem,
  reclaimStaleProcessing,
  technicalOfError,
  todayIso,
  withAiRetry,
  type CompanyContext,
} from './pipeline';
import { reconcileCompany } from './reconcile';

export interface RunOptions {
  concurrency?: 1 | 2 | 3 | undefined;
  signal?: AbortSignal | undefined;
  /** Process only these items (default: every `new` item of the company). */
  itemIds?: Uuid[] | undefined;
}

/** The AI's reading of one receipt, ready to store. */
export type ReceiptReadOutcome = { kind: 'read'; reading: InboxItem['reading']; extraction: ReceiptExtraction } | { kind: 'file_missing' };

/** Read one receipt with the injected reader. Throws PipelineWait / PipelineAbort / Error. */
export async function readReceiptItem(item: InboxItem, ctx: CompanyContext, ai: AiSettings, deps: InboxDeps, db: BookkeepingDB, signal?: AbortSignal): Promise<ReceiptReadOutcome> {
  const attachment = item.attachmentId ? await db.attachments.get(item.attachmentId) : undefined;
  if (!attachment?.blob) return { kind: 'file_missing' };
  const readReceipt = deps.readReceipt;
  if (!readReceipt) throw new PipelineWait('run', WAITING_FOR_AI);
  const bytes = new Uint8Array(await attachment.blob.arrayBuffer());
  const today = (deps.today ?? todayIso)();
  const read = await withAiRetry(
    () =>
      readReceipt(
        { bytes, mediaType: attachment.mediaType, entity: ctx.company.entityType, catalog: ctx.catalog, today, ...(item.note ? { note: item.note } : {}) },
        ai,
        { signal },
      ),
    deps,
    signal,
  );
  return {
    kind: 'read',
    reading: read.reading,
    extraction: { at: new Date().toISOString(), proposal: read.proposal, flags: read.flags },
  };
}

function classificationFrom(c: BankRowClassificationResult): BankRowClassification {
  return { at: new Date().toISOString(), kind: c.kind, categoryId: c.categoryId, vat: c.vat, confidence: c.confidence, counterparty: c.counterparty, needsReceipt: c.needsReceipt };
}

/**
 * Classify bank-row items with the injected AI, one call per batch, and store
 * the classification on each item. Returns the ids that failed (status set
 * to `error`); throws PipelineWait / PipelineAbort for the run to handle.
 */
export async function classifyBankRowItems(items: readonly InboxItem[], ctx: CompanyContext, ai: AiSettings, deps: InboxDeps, db: BookkeepingDB, signal?: AbortSignal): Promise<Uuid[]> {
  const classify = deps.classifyBankRows;
  if (!items.length || !classify) return [];
  const failed: Uuid[] = [];
  const rowIds = items.map((i) => i.bankRowId).filter((id): id is Uuid => !!id);
  const [rows, verifikationer] = await Promise.all([
    db.bankRows.where('id').anyOf(rowIds).toArray(),
    db.verifikationer.where('companyId').equals(ctx.company.id).toArray(),
  ]);
  const rowById = new Map<Uuid, BankRow>(rows.map((r) => [r.id, r]));
  const history = verifikationer
    .filter((v) => v.meta?.source === 'bank' && v.meta.categoryId)
    .slice(-BANK_HISTORY_LIMIT)
    .map((v) => ({ text: v.meta!.counterparty, categoryId: v.meta!.categoryId! }));
  const withRows = items.map((item) => ({ item, row: item.bankRowId ? rowById.get(item.bankRowId) : undefined })).filter((x): x is { item: InboxItem; row: BankRow } => !!x.row);
  for (let i = 0; i < withRows.length; i += BANK_CLASSIFY_BATCH) {
    const batch = withRows.slice(i, i + BANK_CLASSIFY_BATCH);
    if (signal?.aborted) throw new PipelineAbort();
    let results: BankRowClassificationResult[];
    try {
      results = await withAiRetry(
        () =>
          classify(
            batch.map(({ row }, index) => ({ index, date: row.transactionDate ?? row.bookingDate, amountOre: row.amountOre, text: row.text })),
            { entity: ctx.company.entityType, catalog: ctx.catalog, history },
            ai,
            { signal },
          ),
        deps,
        signal,
      );
    } catch (err) {
      if (err instanceof PipelineWait || err instanceof PipelineAbort) throw err;
      const message = err instanceof Error ? err.message : GENERIC_AI_ERROR;
      const technical = technicalOfError(err);
      for (const { item } of batch) {
        await patchItem(db, item.id, { status: 'error', error: message, ...(technical ? { errorTechnical: technical } : {}) });
        failed.push(item.id);
      }
      continue;
    }
    const byIndex = new Map(results.map((r) => [r.index, r]));
    for (const [index, { item }] of batch.entries()) {
      const c = byIndex.get(index);
      if (c) await patchItem(db, item.id, { classification: classificationFrom(c) });
    }
  }
  return failed;
}

async function mapWithConcurrency<T>(items: T[], concurrency: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(3, concurrency)) }, async () => {
    while (next < items.length) {
      const item = items[next++]!;
      await fn(item);
    }
  });
  await Promise.all(workers);
}

function aiUsableFor(ai: AiSettings | null, deps: InboxDeps, what: 'receipts' | 'bankRows'): boolean {
  if (!ai) return false;
  return what === 'receipts' ? deps.readReceipt !== undefined : deps.classifyBankRows !== undefined;
}

/**
 * Process every `new` item of a company (see the module comment). Returns a
 * summary; never throws for per-item failures.
 */
export async function runPipeline(companyId: Uuid, opts: RunOptions, deps: InboxDeps, db: BookkeepingDB = defaultDb): Promise<ProcessSummary> {
  const summary: ProcessSummary = { processed: 0, waiting: 0, failed: 0, aborted: false, changed: 0 };
  const signal = opts.signal;
  const ctx = await loadCompanyContext(companyId, db);
  const aiSettings = await (deps.getAiSettings ?? (async (d: BookkeepingDB) => (await getSettings(d)).ai))(db);
  const ai = aiConfigured(aiSettings) ? aiSettings : null;
  const today: IsoDate = (deps.today ?? todayIso)();

  await reclaimStaleProcessing(db, companyId);

  /*
   * `createdAt` has millisecond resolution, so two receipts added in the same
   * tick sorted arbitrarily — and with a duplicate pair, which one becomes the
   * original and which gets flagged then changed between runs. The id breaks
   * the tie so the same inbox always processes in the same order.
   */
  let pending = (await db.inboxItems.where('[companyId+status]').equals([companyId, 'new']).toArray()).sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
  if (opts.itemIds) {
    const wanted = new Set(opts.itemIds);
    pending = pending.filter((i) => wanted.has(i.id));
  }
  const owned = new Set(pending.map((i) => i.id));
  const toRead = pending.filter((i) => i.kind === 'receipt' && !i.extraction);
  const canRead = aiUsableFor(ai, deps, 'receipts');
  const canClassify = aiUsableFor(ai, deps, 'bankRows');

  if (pending.length) {
    const stamp = new Date().toISOString();
    await db.inboxItems.bulkUpdate(pending.map((i) => ({ key: i.id, changes: { status: 'processing' as const, updatedAt: stamp } })));
  }

  // 1. Everything the stored facts settle.
  const first = await reconcileCompany(companyId, ctx, db, { ownedIds: owned, holdForClassification: canClassify, today });
  summary.changed += first.changed;
  for (const linked of first.linkedDocuments) await fileLinked(linked, deps, db);

  let stopRun: string | null = null;
  const waiting = new Set<Uuid>();
  const failed = new Set<Uuid>();

  // 2a. Bank rows the rules could not place: one AI call per batch.
  const toClassify = canClassify ? await db.inboxItems.where('id').anyOf(first.needsClassification).toArray() : [];
  for (const item of toClassify) owned.add(item.id);
  if (toClassify.length && ai) {
    try {
      for (const id of await classifyBankRowItems(toClassify, ctx, ai, deps, db, signal)) failed.add(id);
    } catch (err) {
      const reason = err instanceof PipelineWait ? err.message : err instanceof PipelineAbort ? undefined : GENERIC_AI_ERROR;
      for (const item of toClassify) {
        if (failed.has(item.id)) continue;
        const fresh = await db.inboxItems.get(item.id);
        if (!fresh?.classification) {
          await patchItem(db, item.id, { status: 'new', ...(reason ? { error: reason } : {}) });
          waiting.add(item.id);
        }
      }
      if (err instanceof PipelineAbort) summary.aborted = true;
      else if (err instanceof PipelineWait && err.scope === 'run') stopRun = err.message;
    }
  }

  // 2b. Receipts nobody has read yet.
  if (toRead.length && !summary.aborted && !stopRun) {
    if (!canRead || !ai) {
      for (const item of toRead) {
        await patchItem(db, item.id, { status: 'new', error: WAITING_FOR_AI });
        waiting.add(item.id);
      }
    } else {
      const concurrency = opts.concurrency ?? aiSettings.parallel ?? 1;
      await mapWithConcurrency(toRead, concurrency, async (item) => {
        if (signal?.aborted || stopRun) {
          await patchItem(db, item.id, { status: 'new', ...(stopRun ? { error: stopRun } : {}) });
          waiting.add(item.id);
          if (signal?.aborted) summary.aborted = true;
          return;
        }
        try {
          const outcome = await readReceiptItem(item, ctx, ai, deps, db, signal);
          if (outcome.kind === 'file_missing') {
            await patchItem(db, item.id, { status: 'needs_you', tier: 'needs_you', tierReasons: [FILE_MISSING], matchedBankRowIds: [], error: undefined });
            owned.delete(item.id);
            summary.processed++;
            return;
          }
          await patchItem(db, item.id, { reading: outcome.reading, extraction: outcome.extraction, error: undefined, errorTechnical: undefined });
        } catch (err) {
          if (err instanceof PipelineAbort) {
            await patchItem(db, item.id, { status: 'new' });
            waiting.add(item.id);
            summary.aborted = true;
          } else if (err instanceof PipelineWait) {
            await patchItem(db, item.id, { status: 'new', error: err.message });
            waiting.add(item.id);
            if (err.scope === 'run') stopRun = err.message;
          } else {
            await patchItem(db, item.id, {
              status: 'error',
              error: err instanceof Error ? err.message : GENERIC_AI_ERROR,
              errorTechnical: technicalOfError(err),
            });
            failed.add(item.id);
          }
        }
      });
    }
  } else if (toRead.length) {
    // The run stopped before the reads: the receipts wait with the reason.
    for (const item of toRead) {
      await patchItem(db, item.id, { status: 'new', ...(stopRun ? { error: stopRun } : {}) });
      waiting.add(item.id);
    }
  }

  // 3. The new facts pair with everything else.
  const still = new Set([...owned].filter((id) => !waiting.has(id) && !failed.has(id)));
  const second = await reconcileCompany(companyId, ctx, db, { ownedIds: still, holdForClassification: false, today });
  summary.changed += second.changed;
  for (const linked of second.linkedDocuments) await fileLinked(linked, deps, db);

  // Nothing owned may stay in flight: a receipt still `processing` here was
  // read but not placed (no reading to reconcile on), so it waits.
  for (const id of still) {
    const item = await db.inboxItems.get(id);
    if (!item) continue;
    if (item.status === 'processing') {
      await patchItem(db, id, { status: 'new' });
      waiting.add(id);
    }
  }
  summary.waiting = waiting.size;
  summary.failed = failed.size;
  summary.processed = [...owned].filter((id) => !waiting.has(id) && !failed.has(id)).length;
  return summary;
}

async function fileLinked(linked: { itemId: Uuid; verifikationId: Uuid }, deps: InboxDeps, db: BookkeepingDB): Promise<void> {
  const item = await db.inboxItems.get(linked.itemId);
  if (item) await fileLateDocument(item, linked.verifikationId, deps, db);
}
